import type { FC } from "react";

import { Button } from "@jti/core-components/components/Button";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { useMessageBus } from "@jti/fe-message-bus";

import styles from "./SearchCard.module.scss";

export type SearchCardProps = {
  img?: string;
  alt?: string;
  title: string;
  description: string;
  linkLabel: string;
  link: string;
  searchValue: string;
  searchType?: string;
};

export const SearchCard: FC<SearchCardProps> = ({
  img,
  alt,
  title,
  description,
  linkLabel,
  link,
  searchValue,
  searchType,
}) => {
  const messageBus = useMessageBus();

  const isSearchPage = searchType === "faq";
  const eventName = isSearchPage ? "faq_search_results_click" : "search_result_click";

  const handleLinkClick = () => {
    messageBus.publish("tracking.event", {
      payload: {
        event_name: eventName,
        additionalData: {
          search_term: searchValue,
        },
      },
    });
  };

  return (
    <div className={styles.searchCard} data-testid="searchCard">
      <div className={styles.imageAndTextSectionWrapper}>
        {img && (
          <div className={styles.imageWrapper}>
            <LinkBehaviour href={link} onClick={handleLinkClick} data-testid="searchCardImageLink">
              <img className={styles.image} src={img} alt={alt} data-testid="searchCardImage" />
            </LinkBehaviour>
          </div>
        )}
        <div className={styles.infoSection}>
          <div className={styles.textContainer}>
            <LinkBehaviour href={link} onClick={handleLinkClick} className={styles.titleLink}>
              <h4 className={styles.title} data-testid="searchCardTitle">
                {title}
              </h4>
            </LinkBehaviour>
            <p className={styles.description} data-testid="searchCardDescription">
              {description}
            </p>
          </div>
          <Button
            className={styles.ctaButton}
            variant="primary"
            href={link}
            onClick={handleLinkClick}
            data-testid="searchCardCtaButton"
            size="lg"
          >
            {linkLabel}
          </Button>
        </div>
      </div>
      <Button
        className={styles.mobileCtaButton}
        variant="primary"
        href={link}
        onClick={handleLinkClick}
        data-testid="searchCardCtaButtonMobile"
        size="sm"
      >
        {linkLabel}
      </Button>
    </div>
  );
};
